// ---------------------------------------------------------------------------
// Heading Renderer
// ---------------------------------------------------------------------------
// Foundational primitive — publishes the editor `Heading` block to a real
// <hN> element instead of a "<!-- unknown component: Heading -->" sentinel.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

type HeadingLevel = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
type HeadingAlign = "left" | "center" | "right";

interface HeadingProps {
  text?: string;
  level?: HeadingLevel | string;
  align?: HeadingAlign | string;
}

const VALID_LEVELS: ReadonlySet<HeadingLevel> = new Set([
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
]);

const VALID_ALIGNMENTS: ReadonlySet<HeadingAlign> = new Set([
  "left",
  "center",
  "right",
]);

function resolveLevel(value: unknown): HeadingLevel {
  if (typeof value === "string") {
    const lower = value.toLowerCase();
    if (VALID_LEVELS.has(lower as HeadingLevel)) {
      return lower as HeadingLevel;
    }
  }
  return "h2";
}

function resolveAlignment(value: unknown): HeadingAlign {
  if (typeof value === "string" && VALID_ALIGNMENTS.has(value as HeadingAlign)) {
    return value as HeadingAlign;
  }
  return "left";
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as HeadingProps;

  const headingText = typeof p.text === "string" ? p.text.trim() : "";
  if (!headingText) {
    return "<!-- heading missing text -->";
  }

  const level = resolveLevel(p.level);
  const alignment = resolveAlignment(p.align);

  return `<section class="ease-section ease-heading ease-heading--${attr(level)}">
  <${level} class="ease-heading__text" style="text-align: ${attr(alignment)};">${text(headingText)}</${level}>
</section>`;
}

registerRenderer("heading", { render });
